import axios from 'axios';
import { logout } from 'actions';
import { store } from './store';
import API from 'actions/api';
import { markTokenRefreshed } from 'components/SessionTimeout/idleTimeout';

const axiosInstance = axios.create();
const lang = localStorage.getItem('i18nextLng') || 'en';
axiosInstance.defaults.headers.common['Accept-Language'] = lang;

const REFRESH_URL = `${API}/token/refresh/`;

// Shared by every request that hits a 401 at the same time, so a burst of
// expired requests triggers one refresh call instead of one each.
let refreshPromise = null;
let sessionExpiring = false;

export const refreshAccessToken = () => {
  if (!refreshPromise) {
    refreshPromise = axiosInstance
      .post(REFRESH_URL, { refresh: localStorage.getItem('refreshToken') })
      .then(({ data }) => {
        localStorage.setItem('token', data.access);
        // Present only when the backend rotates refresh tokens.
        if (data.refresh) {
          localStorage.setItem('refreshToken', data.refresh);
        }
        markTokenRefreshed();
        return data.access;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

// Log the user out and send them to the login page, which offers to return
// them to the page they were on.
export const expireSession = () => {
  if (sessionExpiring) return;
  sessionExpiring = true;

  store.dispatch(logout());
  const encodedPathname = encodeURIComponent(window.location.pathname);
  window.location.href = `/login?sessionExpired=true&path=${encodedPathname}`;
};

axiosInstance.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('token');
    if (accessToken) {
      config.headers['Authorization'] = `Token ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config } = error;
    const status = error?.response?.status;

    if (status !== 401 || !config) {
      return Promise.reject(error);
    }

    // A rejected refresh, or a request that still fails after a refresh,
    // means the session is gone. Never refresh from here, or it loops.
    if (config.url === REFRESH_URL || config._retriedAfterRefresh) {
      return Promise.reject(error);
    }

    // Visitors who were never logged in get the 401 as-is.
    if (!localStorage.getItem('refreshToken')) {
      return Promise.reject(error);
    }

    try {
      const accessToken = await refreshAccessToken();
      config._retriedAfterRefresh = true;
      config.headers['Authorization'] = `Token ${accessToken}`;
      return axiosInstance(config);
    } catch (refreshError) {
      expireSession();
      return Promise.reject(error);
    }
  },
);

export default axiosInstance;
